import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer';

import { dayTotalsOf } from '../utils/freeMeal';

import { SheetFooter, SheetHeader } from './pdf/PdfChrome';
import { C, base, today } from './pdf/pdfTheme';

// The meal plan: daily averages and the week at a glance first, then each day's meals with their foods.
// Portrait, two days to a page, so it can go on the fridge.

const s = StyleSheet.create({
  page: { ...base.page },
  stats: { flexDirection: 'row', marginBottom: 16 },
  stat: { flex: 1, marginRight: 8, padding: 10, borderRadius: 6, backgroundColor: C.surface },
  statLast: { marginRight: 0 },
  statLabel: { fontSize: 7, fontWeight: 700, color: C.ink3, letterSpacing: 0.4, textTransform: 'uppercase' },
  statValue: { marginTop: 4, fontSize: 16, fontWeight: 900, letterSpacing: -0.3 },
  statUnit: { fontSize: 8, fontWeight: 500, color: C.ink2 },
  sectionTitle: { fontSize: 11, fontWeight: 900, marginBottom: 6 },
  // Week at a glance
  week: { marginBottom: 18 },
  weekHead: { flexDirection: 'row', paddingBottom: 4, borderBottomWidth: 0.75, borderBottomColor: C.ink },
  weekRow: { flexDirection: 'row', paddingVertical: 4.5, borderBottomWidth: 0.5, borderBottomColor: C.line },
  weekDay: { width: 74, fontSize: 8.5, fontWeight: 700 },
  weekCell: { flex: 1, fontSize: 8.5, textAlign: 'right', color: C.ink2 },
  weekTotal: { width: 64, fontSize: 8.5, fontWeight: 700, textAlign: 'right' },
  weekFree: { color: C.amber, fontWeight: 700 },
  th: { fontSize: 6.5, fontWeight: 700, color: C.ink2, letterSpacing: 0.5, textTransform: 'uppercase' },
  // A day
  day: { marginBottom: 16 },
  dayHead: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    paddingBottom: 5,
    marginBottom: 6,
    borderBottomWidth: 1.5,
    borderBottomColor: C.green,
  },
  dayName: { fontSize: 13, fontWeight: 900, letterSpacing: -0.2 },
  dayTotals: { fontSize: 8, color: C.ink2 },
  dayKcal: { fontSize: 10, fontWeight: 700, color: C.ink },
  meal: { marginBottom: 7 },
  mealHead: { flexDirection: 'row', alignItems: 'center', marginBottom: 2 },
  mealName: { fontSize: 9, fontWeight: 700 },
  mealKcal: {
    marginLeft: 6,
    paddingHorizontal: 5,
    paddingVertical: 1,
    borderRadius: 6,
    backgroundColor: C.greenTint,
    color: C.greenInk,
    fontSize: 7,
    fontWeight: 700,
  },
  foodRow: { flexDirection: 'row', paddingVertical: 2.5, borderBottomWidth: 0.4, borderBottomColor: C.line },
  foodName: { flex: 1, fontSize: 8.5 },
  foodQty: { width: 120, fontSize: 8, color: C.ink2 },
  foodNum: { width: 34, fontSize: 8, textAlign: 'right', color: C.ink2 },
  foodKcal: { width: 40, fontSize: 8, textAlign: 'right', fontWeight: 700 },
  macroHead: { flexDirection: 'row', paddingTop: 1, paddingBottom: 2 },
  free: { marginTop: 2, padding: 7, borderRadius: 5, backgroundColor: C.amberTint },
  freeTitle: { fontSize: 8.5, fontWeight: 700, color: C.amber },
  freeText: { marginTop: 1, fontSize: 8, color: C.ink2 },
});

const getLabels = (language) =>
  language === 'pt'
    ? {
        brand: 'Meu Treino · Plano alimentar',
        title: 'Plano Alimentar',
        subtitle: (goal, kcal) => [goal, kcal ? `${kcal} kcal por dia` : null].filter(Boolean).join(' · '),
        meta: 'Sete dias de refeições montados para o seu objetivo',
        name: 'Nome',
        start: 'Início',
        average: 'Média diária',
        protein: 'Proteína',
        carbs: 'Carboidratos',
        fat: 'Gorduras',
        week: 'A semana',
        day: 'Dia',
        total: 'Total',
        food: 'Alimento',
        quantity: 'Quantidade',
        kcal: 'kcal',
        p: 'P',
        c: 'C',
        f: 'G',
        free: 'Refeição livre',
        freeShort: 'Livre',
        freeText: 'Coma o que quiser nessa refeição, com moderação.',
        freeTextBudget: 'Coma o que quiser nessa refeição, perto de {kcal} kcal.',
        generated: (date) => `Plano gerado em ${date}`,
        page: (n, total) => `Página ${n} de ${total}`,
        site: 'meutreino.fit',
        locale: 'pt-BR',
      }
    : {
        brand: 'My Workout · Meal plan',
        title: 'Meal Plan',
        subtitle: (goal, kcal) => [goal, kcal ? `${kcal} kcal a day` : null].filter(Boolean).join(' · '),
        meta: 'Seven days of meals built around your goal',
        name: 'Name',
        start: 'Start date',
        average: 'Daily average',
        protein: 'Protein',
        carbs: 'Carbs',
        fat: 'Fat',
        week: 'The week',
        day: 'Day',
        total: 'Total',
        food: 'Food',
        quantity: 'Amount',
        kcal: 'kcal',
        p: 'P',
        c: 'C',
        f: 'F',
        free: 'Free meal',
        freeShort: 'Free',
        freeText: 'Eat whatever you like at this meal, in moderation.',
        freeTextBudget: 'Eat whatever you like at this meal, around {kcal} kcal.',
        generated: (date) => `Plan made on ${date}`,
        page: (n, total) => `Page ${n} of ${total}`,
        site: 'myworkoutskill.com',
        locale: 'en-US',
      };

const sum = (foods) =>
  (foods || []).reduce(
    (acc, food) => ({
      calories: acc.calories + (food.calories || 0),
      protein: acc.protein + (food.protein || 0),
      carbs: acc.carbs + (food.carbs || 0),
      fat: acc.fat + (food.fat || 0),
    }),
    { calories: 0, protein: 0, carbs: 0, fat: 0 }
  );

// The free meal counts with its calorie budget (plans made before the budget have none)
const dayTotals = (day) => dayTotalsOf(day.meals);

const Stat = ({ label, value, unit, last }) => (
  <View style={[s.stat, last && s.statLast]}>
    <Text style={s.statLabel}>{label}</Text>
    <Text style={s.statValue}>
      {value}
      <Text style={s.statUnit}> {unit}</Text>
    </Text>
  </View>
);

// Days down, meals across: each meal's kcal, the free meal flagged, the day's total on the right
const WeekTable = ({ weekPlan, L, n }) => {
  const mealNames = Array.from({ length: Math.max(...weekPlan.map((day) => day.meals.length)) }, (_, i) =>
    weekPlan.map((day) => day.meals[i]).find((meal) => meal && !meal.isFreeMeal)?.name || `${i + 1}`
  );
  return (
    <View style={s.week} wrap={false}>
      <Text style={s.sectionTitle}>{L.week}</Text>
      <View style={s.weekHead}>
        <Text style={[s.th, { width: 74 }]}>{L.day}</Text>
        {mealNames.map((name, i) => (
          <Text key={i} style={[s.th, { flex: 1, textAlign: 'right' }]}>
            {name}
          </Text>
        ))}
        <Text style={[s.th, { width: 64, textAlign: 'right' }]}>{L.total}</Text>
      </View>
      {weekPlan.map((day, d) => (
        <View key={d} style={s.weekRow}>
          <Text style={s.weekDay}>{day.dayName}</Text>
          {mealNames.map((_, i) => {
            const meal = day.meals[i];
            if (!meal) return <Text key={i} style={s.weekCell} />;
            return meal.isFreeMeal ? (
              <Text key={i} style={[s.weekCell, s.weekFree]}>
                {L.freeShort}
              </Text>
            ) : (
              <Text key={i} style={s.weekCell}>
                {n(sum(meal.foods).calories)}
              </Text>
            );
          })}
          <Text style={s.weekTotal}>
            {n(dayTotals(day).calories)} {L.kcal}
          </Text>
        </View>
      ))}
    </View>
  );
};

const DayBlock = ({ day, L, n }) => {
  const totals = dayTotals(day);
  return (
    <View style={s.day} wrap={false}>
      <View style={s.dayHead}>
        <Text style={s.dayName}>{day.dayName}</Text>
        <Text style={s.dayTotals}>
          <Text style={s.dayKcal}>
            {n(totals.calories)} {L.kcal}
          </Text>
          {`   ${L.p} ${n(totals.protein)} g · ${L.c} ${n(totals.carbs)} g · ${L.f} ${n(totals.fat)} g`}
        </Text>
      </View>
      <View style={s.macroHead}>
        <Text style={[s.th, { flex: 1 }]}>{L.food}</Text>
        <Text style={[s.th, { width: 120 }]}>{L.quantity}</Text>
        <Text style={[s.th, { width: 40, textAlign: 'right' }]}>{L.kcal}</Text>
        <Text style={[s.th, { width: 34, textAlign: 'right' }]}>{L.p}</Text>
        <Text style={[s.th, { width: 34, textAlign: 'right' }]}>{L.c}</Text>
        <Text style={[s.th, { width: 34, textAlign: 'right' }]}>{L.f}</Text>
      </View>
      {day.meals.map((meal, m) =>
        meal.isFreeMeal ? (
          <View key={m} style={[s.meal, s.free]}>
            <Text style={s.freeTitle}>{meal.budget ? `${meal.name} · ${L.freeShort}` : L.free}</Text>
            <Text style={s.freeText}>{meal.budget ? L.freeTextBudget.replace('{kcal}', n(meal.budget)) : L.freeText}</Text>
          </View>
        ) : (
          <View key={m} style={s.meal}>
            <View style={s.mealHead}>
              <Text style={s.mealName}>{meal.name}</Text>
              <Text style={s.mealKcal}>
                {n(sum(meal.foods).calories)} {L.kcal}
              </Text>
            </View>
            {(meal.foods || []).map((food, f) => (
              <View key={f} style={s.foodRow}>
                <Text style={s.foodName}>{food.name}</Text>
                <Text style={s.foodQty}>{food.quantity}</Text>
                <Text style={s.foodKcal}>{n(food.calories)}</Text>
                <Text style={s.foodNum}>{n(food.protein)}</Text>
                <Text style={s.foodNum}>{n(food.carbs)}</Text>
                <Text style={s.foodNum}>{n(food.fat)}</Text>
              </View>
            ))}
          </View>
        )
      )}
    </View>
  );
};

// targetCalories and goalLabel are optional: the plan reads fine without them
export const DietPlanPDF = ({ weekPlan, weeklyAverages, language, targetCalories, goalLabel }) => {
  const L = getLabels(language);
  const number = new Intl.NumberFormat(L.locale, { maximumFractionDigits: 0 });
  const n = (value) => number.format(Math.round(value || 0));
  const subtitle = L.subtitle(goalLabel, targetCalories ? n(targetCalories) : null);

  return (
    <Document title={L.title} author={L.site}>
      <Page size="A4" style={s.page}>
        <SheetHeader brand={L.brand} title={L.title} subtitle={subtitle} meta={L.meta} fields={[L.name, L.start]} narrow />
        {weeklyAverages ? (
          <View style={s.stats}>
            <Stat label={L.average} value={n(weeklyAverages.calories)} unit={L.kcal} />
            <Stat label={L.protein} value={n(weeklyAverages.protein)} unit="g" />
            <Stat label={L.carbs} value={n(weeklyAverages.carbs)} unit="g" />
            <Stat label={L.fat} value={n(weeklyAverages.fat)} unit="g" last />
          </View>
        ) : null}
        <WeekTable weekPlan={weekPlan} L={L} n={n} />
        {weekPlan.map((day, d) => (
          <DayBlock key={d} day={day} L={L} n={n} />
        ))}
        <SheetFooter site={L.site} generated={L.generated(today(language))} page={L.page} />
      </Page>
    </Document>
  );
};
